/**
 * Erenown brand orbit (Round 5 — final copy pass).
 *
 * Each brand has:
 *   - tagline:     short positioning line (used as eyebrow/sub-label everywhere)
 *   - description: long paragraph used in side panels and brand previews
 *   - modules:     exactly 4 modules with id, name, body
 *
 * Erenown is described site-wide as the "central structure" — never "flagship".
 */
export const BRANDS = [
  {
    id: "hubinall",
    name: "Hubinall",
    tagline: "Run more as one",
    description:
      "Hubinall is Erenown's structured business platform for launch, operation, connectivity, and decision-ready growth. Built around the idea of a hub in every stage, it helps ventures move from first setup to connected operational maturity through a more coordinated path.",
    modules: [
      {
        id: "setup",
        name: "Setup",
        body:
          "Launch support for businesses entering a new market or building from the ground up. This includes business launch coordination, structured entry support, and optional practical enablement through Hangonin.",
      },
      {
        id: "office",
        name: "Office",
        body:
          "Operational flow for advisory, professional, and business environments that need more structure in daily coordination, internal organization, and service delivery.",
      },
      {
        id: "connect",
        name: "Connect",
        body:
          "Connectivity between businesses, systems, and operating environments — designed to reduce fragmentation and improve flow across practical business processes.",
      },
      {
        id: "intelligence",
        name: "Intelligence",
        body:
          "A higher-value layer focused on visibility, analysis, and decision support — helping ventures move from operation to more informed growth.",
      },
    ],
  },
  {
    id: "hangonin",
    name: "Hangonin",
    tagline: "Stay in. Settle in.",
    description:
      "Hangonin is Erenown's practical landing support line for businesses entering a new market. It helps ventures settle in with more confidence through local presence support, marketplace onboarding, representation coordination, and light operational assistance.",
    modules: [
      {
        id: "landing-support",
        name: "Landing Support",
        body:
          "Practical support for businesses entering a market for the first time, including guided early-stage coordination and structured local readiness.",
      },
      {
        id: "local-presence",
        name: "Local Presence",
        body:
          "Support related to address coordination, office presence, representation arrangements, and other practical needs that help a business establish a functional foothold.",
      },
      {
        id: "marketplace-onboarding",
        name: "Marketplace Onboarding",
        body:
          "Light support for businesses seeking entry into digital selling environments, marketplaces, and practical commercial channels in target markets.",
      },
      {
        id: "light-tech-support",
        name: "Light Tech Support",
        body:
          "Starter-level digital and operational help, including website support, mockups, practical setup assistance, and lightweight digital presence needs.",
      },
    ],
  },
  {
    id: "allinstra",
    name: "Allinstra",
    tagline: "All institutions, one structure",
    description:
      "Allinstra is Erenown's institutional systems line, designed for municipalities, organized industrial zones, public-service organizations, and structured institutions that require clearer operational management, stronger document control, connected workflows, financial and administrative visibility, and decision-support capability.",
    modules: [
      {
        id: "municipality-systems",
        name: "Municipality & Public Service Systems",
        body:
          "Digital management environments for municipalities and public-service organizations that need clearer coordination, service flow, citizen interaction, revenue processes, and administrative structure.",
      },
      {
        id: "industrial-zone-systems",
        name: "Industrial Zone & Utility Systems",
        body:
          "Operational systems for organized industrial zones and similar structured environments, including subscriber management, utility coordination, industrialist portals, administrative visibility, and sector-specific management flows.",
      },
      {
        id: "edrm",
        name: "Electronic Document & Records",
        body:
          "Structured environments for electronic document, archive, and records management, designed to improve traceability, authorization, control, workflow continuity, and institutional order.",
      },
      {
        id: "process-decision",
        name: "Process, Finance & Decision Support",
        body:
          "Structures that strengthen process visibility, financial and administrative reporting, procurement logic, dashboards, and management decision support across institutional operations.",
      },
    ],
  },
  {
    id: "forealco",
    name: "Forealco",
    tagline: "Foreign trade, real access",
    description:
      "Forealco is Erenown's line for foreign trade support, real market access, export-facing operations, and selected strategic sectors. It helps businesses enter markets, move products, build cross-border traction, and explore more demanding operational environments with greater structure.",
    modules: [
      {
        id: "trade-enablement",
        name: "Trade Enablement",
        body:
          "Support for companies that want to begin selling into new markets through structured commercial coordination and intermediary support models.",
      },
      {
        id: "export-operations",
        name: "Export Operations",
        body:
          "Operational support related to export-facing processes, including documentation flow, local support logic, and structured movement across borders.",
      },
      {
        id: "market-access",
        name: "Market Access",
        body:
          "Commercial entry support designed to help companies test, enter, and better understand new markets before or alongside deeper setup steps.",
      },
      {
        id: "strategic-sectors",
        name: "Defence & Strategic Sectors",
        body:
          "A pathway into sectors where technology, operations, and physical environments intersect — including defence-related environments, mining, renewable energy, micromobility, and facility-linked systems.",
      },
    ],
  },
  {
    id: "globedi",
    name: "Globedi",
    tagline: "Interoperability across borders",
    description:
      "Globedi is Erenown's interoperability line, focused on document exchange, digital alignment, and future-ready cross-border flows. It represents a longer-horizon layer within the Erenown orbit, where practical document movement evolves toward more connected digital environments.",
    modules: [
      {
        id: "document-flow",
        name: "Document Flow",
        body:
          "Structured document movement across borders, entities, and operating environments where coordination matters.",
      },
      {
        id: "e-document",
        name: "E-Document",
        body:
          "Digital document evolution designed to reduce friction, improve traceability, and support more connected exchange environments.",
      },
      {
        id: "e-invoice-readiness",
        name: "E-Invoice Readiness",
        body:
          "A future-facing layer that reflects the path from document handling toward more formalized digital invoicing and interoperable transaction logic.",
      },
      {
        id: "digital-interoperability",
        name: "Digital Interoperability",
        body:
          "The broader destination: more compatible digital environments where information and exchange structures become more connected, scalable, and aligned.",
      },
    ],
  },
];

/**
 * Five capability groups, each with a title, body paragraph, and the brand
 * lines they connect to. Replaces the previous four-pillar / sixteen-programme
 * register.
 */
export const CAPABILITY_GROUPS = [
  {
    id: "launch-entry",
    label: "Business Launch & Entry",
    body:
      "Erenown supports businesses at the point of formation, entry, and early operational structuring. This includes launch coordination, practical enablement, and the first layer of support needed to build a credible foundation.",
    lines: ["Hubinall", "Hangonin"],
  },
  {
    id: "operational-flow",
    label: "Operational Structure & Flow",
    body:
      "Beyond launch, Erenown helps shape more organized operating environments — improving flow, coordination, visibility, and the ability to work with greater clarity over time.",
    lines: ["Hubinall"],
  },
  {
    id: "institutional",
    label: "Institutional Systems",
    body:
      "For municipalities, industrial zones, and structured organizations, Erenown offers system-oriented thinking through digital environments that support coordination, process visibility, document control, and better-managed institutional operations.",
    lines: ["Allinstra"],
  },
  {
    id: "trade-access",
    label: "Trade, Market Access & Strategic Growth",
    body:
      "Erenown supports pathways into new markets through trade enablement, export-oriented support, and access to selected sector opportunities where structure, local coordination, and strategic positioning matter.",
    lines: ["Forealco"],
  },
  {
    id: "interoperability",
    label: "Global Digital Interoperability",
    body:
      "Erenown develops a longer-horizon capability around globe digital interoperability, helping organizations move from practical document flow toward more connected, interoperable, and cross-border digital environments.",
    lines: ["Globedi"],
  },
];
